import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { FullPageLoader } from "@/components/ProtectedRoute";

const DECLARATIONS = [
  ["is_parent_or_guardian", "I am the parent or legal guardian of this student."],
  ["is_adult", "I am 18 years of age or older."],
  ["has_authority", "I have the authority to give consent on this student's behalf."],
];

function Check({ id, checked, onChange, children }) {
  return (
    <label className="flex items-start gap-3 rounded-xl border border-ds-plum/15 p-4 cursor-pointer hover:bg-ds-cream">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" data-testid={`consent-${id}-checkbox`} />
      <span className="text-sm text-ds-plumdeep">{children}</span>
    </label>
  );
}

export default function Consent() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: student } = useFetch(`/students/${studentId}`);
  const { data: statement } = useFetch("/public/consent");
  const [form, setForm] = useState({ is_parent_or_guardian: false, is_adult: false, has_authority: false, accepts_statement: false, signature_name: "" });
  const [busy, setBusy] = useState(false);

  if (!student || !statement) return <FullPageLoader />;
  const declared = DECLARATIONS.every(([k]) => form[k]);
  const ready = declared && form.accepts_statement && form.signature_name.trim().length >= 3;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/students/${studentId}/consent`, { ...form, version_number: statement.version_number });
      toast.success(`Consent recorded for ${student.name}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="consent-page">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm font-semibold text-ds-inksoft hover:text-ds-plum" data-testid="consent-back-link"><ArrowLeft className="h-4 w-4" />Back to dashboard</Link>
      <div>
        <p className="eyebrow text-ds-plum">Parental consent - version {statement.version_number}</p>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Consent for {student.name}</h1>
        <p className="mt-2 text-ds-inksoft">Class {student.class_level}. Please read and confirm each step. This is recorded with the date, time and statement version.</p>
      </div>
      <form onSubmit={submit} className="space-y-8">
        <section className="rounded-[24px] bg-white border border-ds-plum/10 p-6 space-y-3">
          <h2 className="font-display text-lg font-semibold">1. Your declaration</h2>
          {DECLARATIONS.map(([k, text]) => <Check key={k} id={k} checked={form[k]} onChange={(v) => setForm({ ...form, [k]: v })}>{text}</Check>)}
        </section>
        <section className={`rounded-[24px] bg-white border border-ds-plum/10 p-6 space-y-4 transition-opacity ${declared ? "" : "opacity-50 pointer-events-none"}`}>
          <h2 className="font-display text-lg font-semibold">2. Consent statement</h2>
          <div className="max-h-80 overflow-y-auto rounded-xl bg-ds-cream border border-ds-plum/15 p-5 text-sm leading-relaxed text-ds-ink whitespace-pre-line" data-testid="consent-statement-text">
            <p className="font-semibold text-ds-plumdeep mb-3">{statement.title}</p>{statement.body}
          </div>
          <Check id="accept" checked={form.accepts_statement} onChange={(v) => setForm({ ...form, accepts_statement: v })}>I have read and accept the parental consent statement above.</Check>
        </section>
        <section className={`rounded-[24px] bg-white border border-ds-plum/10 p-6 space-y-4 transition-opacity ${declared && form.accepts_statement ? "" : "opacity-50 pointer-events-none"}`}>
          <h2 className="font-display text-lg font-semibold">3. Sign with your full name</h2>
          <div className="space-y-2">
            <Label htmlFor="sig">Type your full name as your signature</Label>
            <Input id="sig" placeholder={user?.name} value={form.signature_name} onChange={(e) => setForm({ ...form, signature_name: e.target.value })} className="h-12 font-serif text-xl italic" data-testid="consent-signature-input" />
          </div>
          <p className="flex gap-2 text-xs text-ds-inksoft"><ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" />Your successful payment from your own card or UPI account completes the verification of this consent.</p>
        </section>
        <button type="submit" disabled={!ready || busy} className="ds-btn w-full h-12 inline-flex items-center justify-center gap-2 bg-ds-plum text-white hover:bg-[#3d0232] disabled:opacity-50" data-testid="consent-submit-button">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Record my consent
        </button>
      </form>
    </div>
  );
}
